import { useCallback, useMemo } from 'react';
import { PERMISSIONS } from '@terra/shared-authorization';
import type {
  TunnelRequirement,
  TunnelRequirementEnforcement,
  TunnelUnsupportedReason,
} from '@terra/shared-contracts-internal-network-tunnel';
import { sidebarTabMapping } from '../../components/layout/utils';
import { useMainContentTabs } from '../../store/layoutStore';
import { useSelectedTarget, useTargetType } from '../../store/projectStore';
import { setProxySettingsSubTabOnActiveTab } from '../../utils/setMainContentSettingsSubTab';
import { useTargetTunnelRequirement } from '../api/useTargetTunnelRequirement';
import {
  useInternalNetworkTunnel,
  type InternalNetworkTunnelUiStatus,
} from '../useInternalNetworkTunnel';
import { useInternalNetworkTunnelVisibility } from '../useInternalNetworkTunnelVisibility';
import { usePermissions } from '../usePermissions';

export type {
  TunnelRequirementEnforcement,
  TunnelUnsupportedReason,
} from '@terra/shared-contracts-internal-network-tunnel';

export type TargetTunnelRibbonState =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'idle';
      connected: boolean;
      reconnecting: boolean;
    }
  | {
      kind: 'connectedToTarget';
      networkName: string;
      reconnecting: boolean;
    }
  | {
      kind: 'requiredNotBound';
      tailscaleNetworkId: string;
      networkName: string;
      enforcement: TunnelRequirementEnforcement;
    }
  | {
      kind: 'mismatch';
      tailscaleNetworkId: string;
      networkName: string;
      enforcement: TunnelRequirementEnforcement;
    }
  | {
      kind: 'routingOff';
      tailscaleNetworkId: string;
      networkName: string;
      enforcement: TunnelRequirementEnforcement;
    }
  | {
      kind: 'unscopedHosts';
      networkName: string;
      unscopedHosts: string[];
      primaryHost: string;
    }
  | {
      kind: 'unsupported';
      reason: TunnelUnsupportedReason;
      enforcement: TunnelRequirementEnforcement;
    }
  | {
      kind: 'noPermission';
      networkName: string;
      enforcement: TunnelRequirementEnforcement;
    };

export const resolveTargetTunnelRibbonState = ({
  showTunnelStatus,
  tunnelStatus,
  requirement,
  canExecuteTunnel,
  isLoading = false,
  isError = false,
}: {
  showTunnelStatus: boolean;
  tunnelStatus: InternalNetworkTunnelUiStatus;
  requirement: TunnelRequirement | null;
  canExecuteTunnel: boolean;
  isLoading?: boolean;
  isError?: boolean;
}): TargetTunnelRibbonState => {
  if (!showTunnelStatus) {
    return { kind: 'hidden' };
  }

  if (isLoading && !requirement) {
    return { kind: 'loading' };
  }

  if (isError && !requirement) {
    return { kind: 'error' };
  }

  if (!requirement || requirement.kind === 'notRequired') {
    return {
      kind: 'idle',
      connected: tunnelStatus.connected,
      reconnecting: tunnelStatus.reconnecting === true,
    };
  }

  if (requirement.kind === 'unsupported') {
    return {
      kind: 'unsupported',
      reason: requirement.reason,
      enforcement: requirement.enforcement,
    };
  }

  const sessionActive =
    tunnelStatus.connected || tunnelStatus.reconnecting === true;
  const boundToTargetNetwork =
    sessionActive &&
    tunnelStatus.internalAccessNetworkId === requirement.tailscaleNetworkId;
  const routingEnabled = tunnelStatus.routeTorchThroughSession !== false;

  if (!canExecuteTunnel && (!boundToTargetNetwork || !routingEnabled)) {
    return {
      kind: 'noPermission',
      networkName: requirement.networkName,
      enforcement: requirement.enforcement,
    };
  }

  if (!sessionActive) {
    return {
      kind: 'requiredNotBound',
      tailscaleNetworkId: requirement.tailscaleNetworkId,
      networkName: requirement.networkName,
      enforcement: requirement.enforcement,
    };
  }

  if (!boundToTargetNetwork) {
    return {
      kind: 'mismatch',
      tailscaleNetworkId: requirement.tailscaleNetworkId,
      networkName: requirement.networkName,
      enforcement: requirement.enforcement,
    };
  }

  if (!routingEnabled) {
    return {
      kind: 'routingOff',
      tailscaleNetworkId: requirement.tailscaleNetworkId,
      networkName: requirement.networkName,
      enforcement: requirement.enforcement,
    };
  }

  if (requirement.unscopedHosts.length > 0) {
    return {
      kind: 'unscopedHosts',
      networkName: requirement.networkName,
      unscopedHosts: requirement.unscopedHosts,
      primaryHost: requirement.unscopedHosts[0],
    };
  }

  return {
    kind: 'connectedToTarget',
    networkName: requirement.networkName,
    reconnecting: tunnelStatus.reconnecting === true,
  };
};

export const useTargetTunnelRibbonState = () => {
  const showTunnelStatus = useInternalNetworkTunnelVisibility();
  const { status: tunnelStatus, isPending, start } = useInternalNetworkTunnel();
  const selectedTarget = useSelectedTarget();
  const targetType = useTargetType();
  const { hasPermission } = usePermissions();
  const { navigateInActiveTab } = useMainContentTabs();

  const canExecuteTunnel = useMemo(
    () => hasPermission(PERMISSIONS.EXECUTE_INTERNAL_NETWORK_TUNNEL.name),
    [hasPermission],
  );

  const {
    requirement,
    isLoading: isRequirementLoading,
    isError: isRequirementError,
  } = useTargetTunnelRequirement({
    targetType,
    targetId: selectedTarget?.id,
    enabled: showTunnelStatus,
  });

  const state = useMemo(
    () =>
      resolveTargetTunnelRibbonState({
        showTunnelStatus,
        tunnelStatus,
        requirement,
        canExecuteTunnel,
        isLoading: isRequirementLoading,
        isError: isRequirementError,
      }),
    [
      showTunnelStatus,
      tunnelStatus,
      requirement,
      canExecuteTunnel,
      isRequirementLoading,
      isRequirementError,
    ],
  );

  const connectToRequiredNetwork = useCallback(() => {
    if (state.kind !== 'requiredNotBound' && state.kind !== 'mismatch') {
      return;
    }

    void start(state.tailscaleNetworkId);
  }, [start, state]);

  const openTunnelRoutingSettings = useCallback(() => {
    navigateInActiveTab(sidebarTabMapping['proxy-settings']);
    setProxySettingsSubTabOnActiveTab('upstream');
  }, [navigateInActiveTab]);

  return {
    state,
    isPending,
    connectToRequiredNetwork,
    openTunnelRoutingSettings,
  };
};
